'use client';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Receipt, Plus, X, Trash2, Download, Eye, Send, CheckCircle, Ban, Loader,
  BarChart3, RefreshCw, AlertTriangle, Wallet, Pause, Play, ShieldCheck, ShieldX,
  MailOpen, RotateCcw, XOctagon, Link2, Copy, MessageCircle, Mail, CalendarClock, Banknote, FileMinus,
} from 'lucide-react';
import api from '../../services/api';
import { SalespersonSelect } from '../../components/crm/billing/SalesFinance';
import ItemPicker, { CostCentreSelect } from '../../components/crm/billing/ItemPicker';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { downloadInvoicePdf, previewInvoicePdf } from '../../utils/invoicePdf';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import { useCanManageFinance } from '../../hooks/useFinanceRole';
import BulkActionBar from '../../components/crm/BulkActionBar';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import SearchableSelect from '../../components/crm/SearchableSelect';
import ReceiptModal from '../../components/crm/billing/ReceiptModal';
import InstallmentModal from '../../components/crm/billing/InstallmentModal';
import ReceiptsView from '../../components/crm/billing/ReceiptsView';
import CreditNoteModal from '../../components/crm/billing/CreditNoteModal';
import NotesList from '../../components/crm/billing/NotesList';
import { SUPPORTED_CURRENCIES } from '../../constants/currencies';
import {
  fetchCrmInvoices, createCrmInvoice, deleteCrmInvoice,
  markCrmInvoicePaid, cancelCrmInvoice,
  markCrmInvoiceViewed, markCrmInvoiceRefunded, voidCrmInvoice, generateCrmInvoicePaymentLink,
  recordCrmInvoicePayment, toggleCrmInvoiceRecurringPause, sendCrmInvoiceViaWhatsApp, sendCrmInvoiceViaEmail, sendCrmInvoiceViaInstagram,
  fetchTaxProfiles,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'DRAFT' },
  { value: 'sent', label: 'SENT' },
  { value: 'viewed', label: 'VIEWED' },
  { value: 'partial', label: 'PARTIAL' },
  { value: 'paid', label: 'PAID' },
  { value: 'overdue', label: 'OVERDUE' },
  { value: 'cancelled', label: 'CANCELLED' },
  { value: 'refunded', label: 'REFUNDED' },
  { value: 'void', label: 'VOID' },
];

// Only statuses that have a safe bulk meaning; each runs the same logic
// (and ledger posting) as the single-invoice action.
const BULK_STATUS_OPTIONS = [
  { value: 'sent', label: 'MARK SENT (drafts)' },
  { value: 'paid', label: 'MARK PAID' },
  { value: 'overdue', label: 'MARK OVERDUE' },
  { value: 'void', label: 'VOID (unpaid)' },
  { value: 'cancelled', label: 'CANCEL (drafts)' },
];

const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const STATUS_BADGE = {
  draft: 'sf-badge-neutral',
  sent: 'sf-badge-warning',
  viewed: 'sf-badge-warning',
  paid: 'sf-badge-success',
  partial: 'sf-badge-manager',
  overdue: 'sf-badge-error',
  cancelled: 'sf-badge-neutral',
  refunded: 'sf-badge-neutral',
  void: 'sf-badge-error',
};

const BLANK_FORM = {
  accountId: '', contactId: '', product: '', currency: 'INR', taxRate: 0, discountAmount: 0,
  discountType: 'flat', discountValue: 0, taxProfileId: '',
  placeOfSupply: '', customerGstin: '',
  issueDate: new Date().toISOString().slice(0, 10),
  dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  notes: '', isRecurring: false, costCentreId: '',
  lineItems: [{ description: '', hsnCode: '', quantity: 1, unitPrice: 0, remarks: '' }],
};



// Fills a line from an item, including the item's GST rate for that line.
function applyItem(f, idx, it) {
  return {
    ...f,
    lineItems: f.lineItems.map((x, i) => (i !== idx ? x : it
      ? { ...x, itemId: it._id, taxRate: it.taxRate ?? x.taxRate, description: it.description || it.name, hsnCode: it.hsnCode || x.hsnCode, unitPrice: it.salesPrice || x.unitPrice }
      : { ...x, itemId: null })),
  };
}

export default function InvoicesPage() {
  const canFinance = useCanManageFinance();
  const dispatch = useDispatch();
  const { invoices, invoicesMeta, loading, taxProfiles } = useSelector(state => state.crm);
  const companyCurrency = useCompanyCurrency();
  const bulk = useBulkSelection();

  const [view, setView] = useState('invoices'); // 'invoices' | 'unpaid-report'
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  // Customer credit (limit, hold, terms) for the invoice being created.
  const [credit, setCredit] = useState(null);
  const [dueTouched, setDueTouched] = useState(false);
  useEffect(() => {
    if (!showModal || (!form.accountId && !form.contactId)) { setCredit(null); return; }
    const params = form.accountId ? { accountId: form.accountId } : { contactId: form.contactId };
    api.get('/crm/customer-finance/credit', { params }).then((r) => {
      const c = r.data.credit;
      setCredit(c);
      // Due date follows the customer's payment terms unless set by hand.
      if (!dueTouched && c?.paymentTermsDays !== undefined) {
        setForm((f) => {
          const d = new Date(`${f.issueDate || new Date().toISOString().slice(0, 10)}T00:00:00`);
          d.setDate(d.getDate() + c.paymentTermsDays);
          return { ...f, dueDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` };
        });
      }
    }).catch(() => setCredit(null));
  }, [showModal, form.accountId, form.contactId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [paymentModalInvoice, setPaymentModalInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paidDate: localToday() });
  const [receiptPreset, setReceiptPreset] = useState(null);
  const [installmentInvoice, setInstallmentInvoice] = useState(null);
  const [creditInvoice, setCreditInvoice] = useState(null);
  const [receiptsKey, setReceiptsKey] = useState(0);
  const [bulkDueDate, setBulkDueDate] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  useEffect(() => {
    dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }));
    dispatch(fetchTaxProfiles());
  }, [dispatch, statusFilter]);

  const loadUnpaidReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.get('/crm/invoices/reports/unpaid');
      setReport(res.data);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to load unpaid report');
    } finally {
      setReportLoading(false);
    }
  };

  const [paymentLogs, setPaymentLogs] = useState([]);
  const [paymentLogsLoading, setPaymentLogsLoading] = useState(false);

  const loadPaymentLogs = async () => {
    setPaymentLogsLoading(true);
    try {
      const res = await api.get('/crm/invoices/payment-logs', { params: { limit: 100 } });
      setPaymentLogs(res.data.logs || []);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to load payment logs');
    } finally {
      setPaymentLogsLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'unpaid-report') loadUnpaidReport();
    if (view === 'payment-logs') loadPaymentLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const loadParties = async () => {
    try {
      const [accRes, contactRes] = await Promise.all([
        api.get('/crm/accounts', { params: { limit: 200 } }),
        api.get('/crm/contacts', { params: { limit: 200 } }),
      ]);
      setAccounts(accRes.data.accounts || accRes.data || []);
      setContacts(contactRes.data.contacts || contactRes.data || []);
    } catch (err) {
      // Account/contact pickers stay empty — invoice creation still works with manual entry.
    }
  };

  const openCreate = async () => {
    setForm(BLANK_FORM);
    setDueTouched(false);
    setShowModal(true);
    await loadParties();
  };

  const openReceipt = (preset) => {
    setReceiptPreset(preset);
    if (!accounts.length && !contacts.length) loadParties();
  };

  const addLineItem = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { description: '', hsnCode: '', quantity: 1, unitPrice: 0, remarks: '' }] }));
  const removeLineItem = (idx) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== idx) }));
  const updateLineItem = (idx, field, value) => setForm(f => ({
    ...f,
    lineItems: f.lineItems.map((item, i) => i === idx ? { ...item, [field]: value } : item),
  }));

  const selectedTaxProfile = taxProfiles.find(p => p._id === form.taxProfileId);
  const subtotal = form.lineItems.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0), 0);
  const rawDiscount = Number(form.discountValue) || 0;
  const discountAmount = Math.min(subtotal, form.discountType === 'percentage' ? subtotal * rawDiscount / 100 : rawDiscount);
  const taxableBase = subtotal - discountAmount;
  const effectiveTaxRate = selectedTaxProfile ? Number(selectedTaxProfile.rate) || 0 : Number(form.taxRate) || 0;
  // Without a tax profile each line can have its own GST rate (empty =
  // the document rate); the discount is shared across lines by value.
  const lineRates = form.lineItems.map((l) => (l.taxRate === '' || l.taxRate === null || l.taxRate === undefined ? Number(form.taxRate) || 0 : Number(l.taxRate) || 0));
  const mixedRates = !selectedTaxProfile && new Set(lineRates).size > 1;
  const taxAmount = selectedTaxProfile
    ? (selectedTaxProfile.isInclusive ? taxableBase * effectiveTaxRate / (100 + effectiveTaxRate) : taxableBase * effectiveTaxRate / 100)
    : form.lineItems.reduce((s, l, i) => s + (subtotal > 0 ? ((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) * taxableBase / subtotal) : 0) * lineRates[i] / 100, 0);
  const total = selectedTaxProfile?.isInclusive ? Math.max(0, taxableBase) : Math.max(0, taxableBase + taxAmount);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.accountId && !form.contactId) return alertService.error('Error', 'Select an account or contact');
    if (form.lineItems.some(i => !i.description.trim())) return alertService.error('Error', 'Every line item needs a description');

    setSaving(true);
    try {
      // accountId/contactId default to '' (both selects start on "None") — sent
      // as-is, an empty string fails Mongoose ObjectId casting server-side even
      // when the other one is set, so an invoice can never be created against
      // just an Account (or just a Contact) without this.
      const payload = { ...form };
      if (!payload.accountId) delete payload.accountId;
      if (!payload.contactId) delete payload.contactId;
      if (!payload.taxProfileId) delete payload.taxProfileId;
      await dispatch(createCrmInvoice(payload)).unwrap();
      alertService.success('Success', 'Invoice created');
      setShowModal(false);
    } catch (err) {
      alertService.error('Error', err || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (invoice) => {
    const confirmed = await alertService.confirm('Delete Invoice?', `Permanently delete "${invoice.invoiceNumber}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteCrmInvoice(invoice._id)).unwrap();
      alertService.success('Deleted', 'Invoice deleted');
    } catch (err) {
      alertService.error('Error', err || 'Failed to delete invoice');
    }
  };

  // Sending checks credit: a warning is shown; a block (409) can be
  // overridden by finance roles after confirming.
  const handleMarkSent = async (invoice, overrideCredit = false) => {
    try {
      const { data } = await api.post(`/crm/invoices/${invoice._id}/send`, overrideCredit ? { overrideCredit: true } : {});
      if (data.creditWarning) alertService.error(data.creditOverridden ? 'Sent over the credit limit' : 'Credit warning', data.creditWarning);
      dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }));
    } catch (err) {
      const body = err.response?.data || {};
      if (body.code === 'CREDIT_LIMIT' && canFinance && !overrideCredit) {
        const go = await alertService.confirm('Credit limit', `${body.message}`, 'warning', 'Send anyway');
        if (go) handleMarkSent(invoice, true);
        return;
      }
      alertService.error('Not sent', body.message || 'Failed to send invoice');
    }
  };
  const [sendingWhatsAppId, setSendingWhatsAppId] = useState(null);
  const handleSendViaWhatsApp = async (invoice) => {
    const confirmed = await alertService.confirm(
      'Send via WhatsApp?',
      `Send invoice "${invoice.invoiceNumber}" as a PDF attachment over WhatsApp to ${invoice.accountId?.phone || invoice.contactId?.phoneNumber || 'this customer'}?`,
      'info', 'Send'
    );
    if (!confirmed) return;
    setSendingWhatsAppId(invoice._id);
    try {
      await dispatch(sendCrmInvoiceViaWhatsApp({ id: invoice._id })).unwrap();
      alertService.success('Sent', 'Invoice sent over WhatsApp');
      dispatch(fetchCrmInvoices());
    } catch (err) {
      alertService.error('Could not send', err || 'Failed to send invoice via WhatsApp');
    } finally {
      setSendingWhatsAppId(null);
    }
  };
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const handleSendViaEmail = async (invoice) => {
    const confirmed = await alertService.confirm(
      'Send via Email?',
      `Email invoice "${invoice.invoiceNumber}" as a PDF attachment to ${invoice.accountId?.email || invoice.contactId?.email || 'this customer'}?`,
      'info', 'Send'
    );
    if (!confirmed) return;
    setSendingEmailId(invoice._id);
    try {
      await dispatch(sendCrmInvoiceViaEmail({ id: invoice._id })).unwrap();
      alertService.success('Sent', 'Invoice emailed');
      dispatch(fetchCrmInvoices());
    } catch (err) {
      alertService.error('Could not send', err || 'Failed to send invoice via email');
    } finally {
      setSendingEmailId(null);
    }
  };
  const [sendingInstagramId, setSendingInstagramId] = useState(null);
  const handleSendViaInstagram = async (invoice) => {
    const confirmed = await alertService.confirm(
      'Send via Instagram?',
      `Send invoice "${invoice.invoiceNumber}" as a link over Instagram DM to this contact?`,
      'info', 'Send'
    );
    if (!confirmed) return;
    setSendingInstagramId(invoice._id);
    try {
      await dispatch(sendCrmInvoiceViaInstagram({ id: invoice._id })).unwrap();
      alertService.success('Sent', 'Invoice sent via Instagram');
      dispatch(fetchCrmInvoices());
    } catch (err) {
      alertService.error('Could not send', err || 'Failed to send invoice via Instagram');
    } finally {
      setSendingInstagramId(null);
    }
  };
  const handleMarkPaid = async (invoice) => {
    const confirmed = await alertService.confirm('Mark Paid?', `Mark "${invoice.invoiceNumber}" as paid?`, 'success', 'Mark Paid');
    if (confirmed) dispatch(markCrmInvoicePaid({ id: invoice._id }));
  };
  const handleCancel = (invoice) => dispatch(cancelCrmInvoice(invoice._id));
  const handleMarkViewed = (invoice) => dispatch(markCrmInvoiceViewed(invoice._id));

  const handleMarkRefunded = async (invoice) => {
    const confirmed = await alertService.confirm('Refund Invoice?', `Mark "${invoice.invoiceNumber}" as refunded?`, 'warning', 'Mark Refunded');
    if (confirmed) dispatch(markCrmInvoiceRefunded({ id: invoice._id, reason: '' }));
  };

  const handleVoid = async (invoice) => {
    const confirmed = await alertService.confirm('Void Invoice?', `Void "${invoice.invoiceNumber}"? This cannot be undone.`, 'danger', 'Void Invoice');
    if (confirmed) dispatch(voidCrmInvoice({ id: invoice._id, reason: '' }));
  };

  const openPaymentModal = (invoice) => {
    setPaymentModalInvoice(invoice);
    setPaymentForm({ amount: '', paidDate: localToday() });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) return alertService.error('Error', 'Enter a valid payment amount');

    setRecordingPayment(true);
    try {
      const due = Math.round(((paymentModalInvoice.totalAmount || 0) - (paymentModalInvoice.amountPaid || 0) - (paymentModalInvoice.amountCredited || 0)) * 100) / 100;
      await dispatch(recordCrmInvoicePayment({ id: paymentModalInvoice._id, amount, paidDate: paymentForm.paidDate })).unwrap();
      const extra = Math.round((Number(amount) - due) * 100) / 100;
      alertService.success('Payment recorded', extra > 0
        ? `${formatCurrency(extra, paymentModalInvoice.currency)} more than was owed is kept as a customer advance — apply it from the Receipts tab.`
        : '');
      setPaymentModalInvoice(null);
    } catch (err) {
      alertService.error('Error', err || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleToggleRecurringPause = (invoice) => dispatch(toggleCrmInvoiceRecurringPause(invoice._id));

  const [paymentLinkModalInvoice, setPaymentLinkModalInvoice] = useState(null);
  const [paymentLinkGateway, setPaymentLinkGateway] = useState('razorpay');
  const [generatingLink, setGeneratingLink] = useState(false);

  const openPaymentLinkModal = (invoice) => {
    setPaymentLinkModalInvoice(invoice);
    setPaymentLinkGateway(invoice.paymentLink?.gateway || 'razorpay');
  };

  const handleGeneratePaymentLink = async () => {
    setGeneratingLink(true);
    try {
      const updated = await dispatch(generateCrmInvoicePaymentLink({ id: paymentLinkModalInvoice._id, gateway: paymentLinkGateway })).unwrap();
      setPaymentLinkModalInvoice(updated);
      alertService.success('Link Generated', 'Payment link created');
    } catch (err) {
      alertService.error('Error', err || 'Failed to generate payment link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyPaymentLink = (url) => {
    navigator.clipboard.writeText(url);
    alertService.toast('success', 'Link copied');
  };

  const [eInvoiceLoadingId, setEInvoiceLoadingId] = useState(null);

  const handleGenerateEInvoice = async (invoice) => {
    setEInvoiceLoadingId(invoice._id);
    try {
      await api.post(`/crm/invoices/${invoice._id}/e-invoice`);
      alertService.success('e-Invoice generated', `IRN issued for ${invoice.invoiceNumber}`);
      dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }));
    } catch (err) {
      alertService.error('e-Invoice failed', err.response?.data?.message || 'Failed to generate e-Invoice');
    } finally {
      setEInvoiceLoadingId(null);
    }
  };

  const handleCancelEInvoice = async (invoice) => {
    const confirmed = await alertService.confirm('Cancel e-Invoice?', `Cancel the IRN for "${invoice.invoiceNumber}"? This cannot be undone and is only valid within 24 hours of generation.`, 'danger', 'Cancel e-Invoice');
    if (!confirmed) return;
    setEInvoiceLoadingId(invoice._id);
    try {
      await api.post(`/crm/invoices/${invoice._id}/e-invoice/cancel`, { reason: 'Cancelled by supplier' });
      alertService.success('e-Invoice cancelled', `IRN cancelled for ${invoice.invoiceNumber}`);
      dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }));
    } catch (err) {
      alertService.error('Cancellation failed', err.response?.data?.message || 'Failed to cancel e-Invoice');
    } finally {
      setEInvoiceLoadingId(null);
    }
  };

  const handleExportInvoices = async () => {
    try {
      const response = await api.get('/crm/invoices/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export invoices');
    }
  };

  const handleDownloadPdf = async (invoice) => {
    setDownloadingId(invoice._id);
    await downloadInvoicePdf(invoice);
    setDownloadingId(null);
  };

  const handlePreviewPdf = async (invoice) => {
    setPreviewingId(invoice._id);
    await previewInvoicePdf(invoice);
    setPreviewingId(null);
  };

  const runBulkAction = async (action, value) => {
    try {
      const res = await api.post('/crm/invoices/bulk', { invoiceIds: bulk.selectedIds, action, value });
      const count = res.data?.processed ?? res.data?.modifiedCount ?? bulk.selectedCount;
      const failed = res.data?.failed || [];
      bulk.clear();
      dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }));
      const done = `${count} invoice(s) ${action === 'delete' ? 'deleted' : 'updated'}`;
      if (failed.length) {
        const list = failed.slice(0, 6).map((f) => `${f.invoiceNumber}: ${f.reason}`).join('\n');
        alertService.error(`${done}, ${failed.length} skipped`, `${list}${failed.length > 6 ? `\n…and ${failed.length - 6} more` : ''}`);
      } else {
        alertService.success(action === 'delete' ? 'Deleted' : 'Updated', done);
      }
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk action failed');
    }
  };

  return (
    <div className={PAGE}>
      <PageHeader title="Customer Invoices" subtitle={<>{invoicesMeta.total} invoices</>} icon={Receipt} actions={<>
          <button onClick={handleExportInvoices} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2">
            <Download size={14} /> Export CSV
          </button>
          {canFinance && <button onClick={() => openReceipt({})} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2">
            <Banknote size={14} /> Record Receipt
          </button>}
          <button onClick={openCreate} className="h-9 px-4 bg-[#0176D3] text-white rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2">
            <Plus size={14} /> New Invoice
          </button>
      </>} />

      <div className="flex gap-1 border-b border-[#D8DDE6] overflow-x-auto">
        <button
          onClick={() => setView('invoices')}
          className={cn(
            'shrink-0 whitespace-nowrap px-4 h-10 text-[13px] font-bold flex items-center gap-2 border-b-2 -mb-px transition-all',
            view === 'invoices' ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B] hover:text-[#2E2E2E]'
          )}
        >
          <Receipt size={14} /> All Invoices
        </button>
        <button
          onClick={() => setView('unpaid-report')}
          className={cn(
            'shrink-0 whitespace-nowrap px-4 h-10 text-[13px] font-bold flex items-center gap-2 border-b-2 -mb-px transition-all',
            view === 'unpaid-report' ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B] hover:text-[#2E2E2E]'
          )}
        >
          <BarChart3 size={14} /> Unpaid Report
        </button>
        <button
          onClick={() => setView('payment-logs')}
          className={cn(
            'shrink-0 whitespace-nowrap px-4 h-10 text-[13px] font-bold flex items-center gap-2 border-b-2 -mb-px transition-all',
            view === 'payment-logs' ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B] hover:text-[#2E2E2E]'
          )}
        >
          <Link2 size={14} /> Payment Logs
        </button>
        <button
          onClick={() => setView('receipts')}
          className={cn(
            'shrink-0 whitespace-nowrap px-4 h-10 text-[13px] font-bold flex items-center gap-2 border-b-2 -mb-px transition-all',
            view === 'receipts' ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B] hover:text-[#2E2E2E]'
          )}
        >
          <Banknote size={14} /> Receipts
        </button>
        <button
          onClick={() => setView('credit-notes')}
          className={cn(
            'shrink-0 whitespace-nowrap px-4 h-10 text-[13px] font-bold flex items-center gap-2 border-b-2 -mb-px transition-all',
            view === 'credit-notes' ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B] hover:text-[#2E2E2E]'
          )}
        >
          <FileMinus size={14} /> Credit Notes
        </button>
      </div>

      {view === 'unpaid-report' ? (
        <UnpaidReport report={report} loading={reportLoading} onRefresh={loadUnpaidReport} />
      ) : view === 'payment-logs' ? (
        <PaymentLogsView logs={paymentLogs} loading={paymentLogsLoading} onRefresh={loadPaymentLogs} />
      ) : view === 'credit-notes' ? (
        <NotesList kind="credit" refreshKey={receiptsKey} onChanged={() => dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }))} />
      ) : view === 'receipts' ? (
        <ReceiptsView refreshKey={receiptsKey} onChanged={() => dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }))} />
      ) : (
      <>
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 flex items-center gap-3 shadow-sm">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-9 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[12px] font-bold uppercase outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <BulkActionBar
        selectedCount={bulk.selectedCount}
        onClear={bulk.clear}
        statusOptions={canFinance ? BULK_STATUS_OPTIONS : BULK_STATUS_OPTIONS.filter((o) => !['paid', 'void'].includes(o.value))}
        statusLabel="Action"
        onStatusChange={(value) => runBulkAction('change_status', value)}
        onDelete={() => runBulkAction('delete')}
        entityLabel="invoice"
      >
        <div className="flex items-center gap-1">
          <input type="date" aria-label="New due date" value={bulkDueDate} onChange={(e) => setBulkDueDate(e.target.value)} className="h-8 px-2 bg-white text-[#2E2E2E] rounded text-[12px] outline-none" />
          <button type="button" disabled={!bulkDueDate} onClick={() => runBulkAction('change_due_date', bulkDueDate)} className="h-8 px-3 bg-[#0176D3] rounded text-[12px] font-semibold disabled:opacity-50">Set due date</button>
        </div>
      </BulkActionBar>

      <div className="sf-table-container">
        <table className="sf-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={bulk.isAllSelected(invoices.map(i => i._id))}
                  onChange={() => bulk.toggleAll(invoices.map(i => i._id))}
                  aria-label="Select all invoices"
                  className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                />
              </th>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Due Date</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && invoices.length === 0 ? (
              <tr><td colSpan={8} className="py-16 text-center"><Loader className="mx-auto animate-spin text-[#0176D3]" size={28} /></td></tr>
            ) : invoices.length > 0 ? invoices.map(invoice => (
              <tr key={invoice._id} className="group">
                <td>
                  <input
                    type="checkbox"
                    checked={bulk.selected.has(invoice._id)}
                    onChange={() => bulk.toggle(invoice._id)}
                    aria-label={`Select ${invoice.invoiceNumber}`}
                    className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                  />
                </td>
                <td>
                  <span className="font-bold text-[#2E2E2E]">{invoice.invoiceNumber}</span>
                  {invoice.eInvoice?.status === 'generated' && (
                    <span title={`e-Invoice IRN: ${invoice.eInvoice.irn}`} className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-[#2E844A]"><ShieldCheck size={11} /></span>
                  )}
                  {invoice.eInvoice?.status === 'failed' && (
                    <span title={`e-Invoice failed: ${invoice.eInvoice.error}`} className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-[#BA0517]"><ShieldX size={11} /></span>
                  )}
                  {invoice.isRecurring && (
                    <>
                      <span title="Recurring monthly invoice" className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-[#0176D3]"><RefreshCw size={10} /></span>
                      <button
                        onClick={() => handleToggleRecurringPause(invoice)}
                        title={invoice.recurringPaused ? 'Resume recurring billing' : 'Pause recurring billing'}
                        className={cn('ml-1 p-0.5 rounded hover:bg-[#F3F2F2]', invoice.recurringPaused ? 'text-[#B35F00]' : 'text-[#706E6B]')}
                      >
                        {invoice.recurringPaused ? <Play size={11} /> : <Pause size={11} />}
                      </button>
                    </>
                  )}
                </td>
                <td>{invoice.accountId?.name || invoice.contactId?.name || '—'}</td>
                <td className="text-[#706E6B]">{invoice.product || '—'}</td>
                <td className="font-bold tabular-nums">
                  {formatCurrency(invoice.totalAmount, invoice.currency)}
                  {invoice.amountPaid > 0 && invoice.status !== 'paid' && (
                    <div className="text-[10px] font-medium text-[#706E6B] normal-case">{formatCurrency(invoice.amountPaid, invoice.currency)} paid</div>
                  )}
                  {invoice.amountCredited > 0 && (
                    <div className="text-[10px] font-medium text-[#706E6B] normal-case">{formatCurrency(invoice.amountCredited, invoice.currency)} credited</div>
                  )}
                </td>
                <td><span className={cn('sf-badge', STATUS_BADGE[invoice.status])}>{invoice.status.toUpperCase()}</span></td>
                <td>
                  {invoice.nextInstallment ? (
                    <span title="Next instalment due">
                      {new Date(invoice.nextInstallment.dueDate).toLocaleDateString()}
                      <span className="block text-[10px] font-semibold text-[#0176D3]">EMI · {formatCurrency(invoice.nextInstallment.amountDue, invoice.currency)} due</span>
                    </span>
                  ) : new Date(invoice.dueDate).toLocaleDateString()}
                </td>
                <td className="text-right">
                  <RowActionsMenu actions={[
                    { label: 'Mark Sent', icon: Send, onClick: () => handleMarkSent(invoice), hidden: invoice.status !== 'draft' },
                    {
                      label: sendingWhatsAppId === invoice._id ? 'Sending…' : 'Send via WhatsApp',
                      icon: MessageCircle, disabled: sendingWhatsAppId === invoice._id,
                      onClick: () => handleSendViaWhatsApp(invoice),
                      hidden: !(invoice.accountId?.phone || invoice.contactId?.phoneNumber) || ['cancelled', 'void'].includes(invoice.status),
                    },
                    {
                      label: sendingEmailId === invoice._id ? 'Sending…' : 'Send via Email',
                      icon: Mail, disabled: sendingEmailId === invoice._id,
                      onClick: () => handleSendViaEmail(invoice),
                      hidden: !(invoice.accountId?.email || invoice.contactId?.email) || ['cancelled', 'void'].includes(invoice.status),
                    },
                    {
                      label: sendingInstagramId === invoice._id ? 'Sending…' : 'Send via Instagram',
                      icon: Send, disabled: sendingInstagramId === invoice._id,
                      onClick: () => handleSendViaInstagram(invoice),
                      hidden: !invoice.contactId?.instagramId || ['cancelled', 'void'].includes(invoice.status),
                    },
                    { label: 'Mark Viewed', icon: MailOpen, onClick: () => handleMarkViewed(invoice), hidden: invoice.status !== 'sent' },
                    { label: 'Record Payment', icon: Wallet, onClick: () => openPaymentModal(invoice), hidden: !canFinance || !['sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) },
                    { label: 'Receive with other invoices', icon: Banknote, onClick: () => openReceipt({ accountId: invoice.accountId?._id, contactId: invoice.accountId ? undefined : invoice.contactId?._id, invoiceId: invoice._id }), hidden: !canFinance || !['sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) },
                    { label: 'Issue Credit Note', icon: FileMinus, onClick: () => setCreditInvoice(invoice), hidden: !canFinance || !['sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) },
                    { label: invoice.installments?.length ? 'Instalment plan (EMI)' : 'Set up EMI plan', icon: CalendarClock, onClick: () => setInstallmentInvoice(invoice), hidden: ['paid', 'cancelled', 'refunded', 'void'].includes(invoice.status) && !invoice.installments?.length },
                    { label: 'Online Payment Link', icon: Link2, onClick: () => openPaymentLinkModal(invoice), hidden: ['paid', 'cancelled', 'refunded', 'void'].includes(invoice.status) },
                    { label: 'Mark Paid', icon: CheckCircle, onClick: () => handleMarkPaid(invoice), hidden: !canFinance || !['sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) },
                    { label: 'Mark Refunded', icon: RotateCcw, onClick: () => handleMarkRefunded(invoice), hidden: !canFinance || !['paid', 'partial'].includes(invoice.status) },
                    { label: 'Void', icon: XOctagon, danger: true, onClick: () => handleVoid(invoice), hidden: !canFinance || ['draft', 'paid', 'cancelled', 'refunded', 'void'].includes(invoice.status) || invoice.amountPaid > 0 || invoice.amountCredited > 0 },
                    { label: 'Cancel', icon: Ban, onClick: () => handleCancel(invoice), hidden: invoice.status !== 'draft' },
                    {
                      label: eInvoiceLoadingId === invoice._id ? 'Generating…' : 'Generate GST e-Invoice',
                      icon: ShieldCheck, disabled: eInvoiceLoadingId === invoice._id,
                      onClick: () => handleGenerateEInvoice(invoice),
                      hidden: !(invoice.customerGstin && invoice.currency === 'INR' && (!invoice.eInvoice || ['not_applicable', 'failed'].includes(invoice.eInvoice.status))),
                    },
                    {
                      label: eInvoiceLoadingId === invoice._id ? 'Cancelling…' : 'Cancel e-Invoice',
                      icon: ShieldX, danger: true, disabled: eInvoiceLoadingId === invoice._id,
                      onClick: () => handleCancelEInvoice(invoice),
                      hidden: invoice.eInvoice?.status !== 'generated',
                    },
                    { label: previewingId === invoice._id ? 'Opening…' : 'Preview PDF', icon: Eye, disabled: previewingId === invoice._id, onClick: () => handlePreviewPdf(invoice) },
                    { label: downloadingId === invoice._id ? 'Downloading…' : 'Download PDF', icon: Download, disabled: downloadingId === invoice._id, onClick: () => handleDownloadPdf(invoice) },
                    { label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(invoice) },
                  ]} />
                </td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="py-24 text-center">
                <Receipt className="mx-auto mb-4 opacity-10" size={48} />
                <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">No invoices yet</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-2xl z-10 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">New Invoice</h3>
              <button onClick={() => setShowModal(false)} className="text-[#706E6B] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>

            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Account</label>
                  <SearchableSelect
                    value={form.accountId}
                    onChange={(accountId) => {
                      // Pre-fill GST fields from the selected account's state/GSTIN on
                      // file — still editable below in case this invoice needs an
                      // override (e.g. billing a different branch/state).
                      const account = accounts.find(a => a._id === accountId);
                      setForm(f => ({
                        ...f,
                        accountId,
                        placeOfSupply: account?.state || f.placeOfSupply,
                        customerGstin: account?.gstin || f.customerGstin,
                      }));
                    }}
                    options={accounts}
                    placeholder="Search accounts…"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Contact</label>
                  <SearchableSelect
                    value={form.contactId}
                    onChange={(contactId) => setForm(f => ({ ...f, contactId }))}
                    options={contacts}
                    placeholder="Search contacts…"
                  />
                </div>
              </div>
              {credit && (credit.creditHold || credit.creditLimit !== null) && (() => {
                const after = credit.outstanding + total;
                const over = credit.creditLimit !== null && after > credit.creditLimit + 0.005;
                const tone = credit.creditHold || over ? 'bg-[#FEF1EE] border-[#F8B6A8] text-[#8C1B0F]' : 'bg-[#F3F9FE] border-[#B9D8F5] text-[#0B4F8A]';
                return (
                  <div className={cn('text-[12px] px-3 py-2 rounded border', tone)}>
                    {credit.creditHold
                      ? <>On credit hold{credit.creditHoldReason ? ` (${credit.creditHoldReason})` : ''} — you can save this invoice, but it can't be sent until the hold is lifted.</>
                      : <>Credit limit {formatCurrency(credit.creditLimit, credit.currency)} · owes {formatCurrency(credit.outstanding, credit.currency)}{credit.overdue ? ` (${formatCurrency(credit.overdue, credit.currency)} overdue)` : ''}
                        {over ? <> · this invoice takes them <strong>{formatCurrency(after - credit.creditLimit, credit.currency)} over</strong>{credit.overLimitAction === 'block' ? ' — sending will need a finance manager' : ''}</> : <> · {formatCurrency(credit.creditLimit - after, credit.currency)} left after this invoice</>}</>}
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Product / Service</label>
                  <input
                    value={form.product}
                    onChange={e => setForm(f => ({ ...f, product: e.target.value }))}
                    placeholder="e.g. Fintech Platform, ChatBot, CRM"
                    className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
                <div className="space-y-1.5 flex flex-col justify-end">
                  <label className="h-10 flex items-center gap-2 text-[13px] font-medium text-[#2E2E2E] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isRecurring}
                      onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))}
                      className="w-4 h-4 accent-[#0176D3]"
                    />
                    Bill monthly (recurring)
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Place of Supply (State)</label>
                  <input
                    value={form.placeOfSupply}
                    onChange={e => setForm(f => ({ ...f, placeOfSupply: e.target.value }))}
                    placeholder="e.g. Karnataka"
                    className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Customer GSTIN (for GST / e-Invoice)</label>
                  <input
                    value={form.customerGstin}
                    onChange={e => setForm(f => ({ ...f, customerGstin: e.target.value.toUpperCase() }))}
                    placeholder="e.g. 29AAAAA0000A1Z5"
                    className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Line Items</label>
                  <button type="button" onClick={addLineItem} className="h-7 px-2.5 bg-[#EAF4FE] text-[#0176D3] rounded text-[11px] font-semibold hover:bg-[#D6EAFB] flex items-center gap-1"><Plus size={12} /> Add Item</button>
                </div>
                {form.lineItems.map((item, idx) => (
                  <div key={idx} className="p-2.5 bg-[#F9FAFB] border border-[#D8DDE6] rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <ItemPicker value={item.itemId} onPick={(it) => setForm(f => applyItem(f, idx, it))} />
                      <input
                        value={item.description}
                        onChange={e => updateLineItem(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1 h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                      />
                      <input
                        type="number" min="0" step="0.01" value={item.quantity}
                        onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-16 h-9 px-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                        title="Quantity"
                      />
                      <input
                        type="number" min="0" step="0.01" value={item.unitPrice}
                        onChange={e => updateLineItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className="w-24 h-9 px-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                        title="Unit Price"
                      />
                      {!form.taxProfileId && (
                        <input
                          type="number" min="0" max="100" step="0.01" value={item.taxRate ?? ''}
                          onChange={e => updateLineItem(idx, 'taxRate', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          placeholder={`${Number(form.taxRate) || 0}%`}
                          className="w-16 h-9 px-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                          title="GST % for this line (empty = the rate below)"
                          aria-label="GST % for this line"
                        />
                      )}
                      <button type="button" onClick={() => removeLineItem(idx)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded shrink-0"><Trash2 size={14} /></button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        value={item.hsnCode || ''}
                        onChange={e => updateLineItem(idx, 'hsnCode', e.target.value)}
                        placeholder="HSN/SAC code"
                        className="w-32 h-8 px-2.5 bg-white border border-[#D8DDE6] rounded text-[12px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                      />
                      <input
                        value={item.remarks || ''}
                        onChange={e => updateLineItem(idx, 'remarks', e.target.value)}
                        placeholder="Remarks (optional)"
                        className="flex-1 h-8 px-2.5 bg-white border border-[#D8DDE6] rounded text-[12px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <CostCentreSelect value={form.costCentreId} onChange={(costCentreId) => setForm(f => ({ ...f, costCentreId }))} className="w-full h-10 bg-[#F3F2F2]" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <SalespersonSelect value={form.salespersonId} onChange={(salespersonId) => setForm(f => ({ ...f, salespersonId }))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Tax Profile</label>
                  <select value={form.taxProfileId} onChange={e => setForm(f => ({ ...f, taxProfileId: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                    <option value="">None (India GST)</option>
                    {taxProfiles.filter(p => p.isActive).map(p => <option key={p._id} value={p._id}>{p.name} ({p.rate}%)</option>)}
                  </select>
                </div>
              </div>

              {!form.taxProfileId && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Tax Rate (%) — India GST</label>
                  <input type="number" min="0" max="100" step="0.01" value={form.taxRate} onChange={e => setForm(f => ({ ...f, taxRate: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Discount Type</label>
                  <select value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                    <option value="flat">Flat Amount</option>
                    <option value="percentage">Percentage</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Discount {form.discountType === 'percentage' ? '(%)' : `(${form.currency})`}</label>
                  <input type="number" min="0" step="0.01" value={form.discountValue} onChange={e => setForm(f => ({ ...f, discountValue: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Issue Date</label>
                  <input type="date" value={form.issueDate} onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Due Date</label>
                  <input type="date" required value={form.dueDate} onChange={e => { setDueTouched(true); setForm(f => ({ ...f, dueDate: e.target.value })); }} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                  {credit && <p className="text-[11px] text-[#706E6B]">{credit.ownTerms ? 'Customer terms' : 'Default terms'}: {credit.paymentTermsDays} days</p>}
                </div>
              </div>

              <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] space-y-1 text-[13px]">
                <div className="flex justify-between"><span className="text-[#706E6B]">Subtotal</span><span className="font-bold">{formatCurrency(subtotal, form.currency)}</span></div>
                <div className="flex justify-between"><span className="text-[#706E6B]">Discount</span><span className="font-bold">-{formatCurrency(discountAmount, form.currency)}</span></div>
                <div className="flex justify-between"><span className="text-[#706E6B]">{selectedTaxProfile ? `${selectedTaxProfile.name} (${effectiveTaxRate}%)` : mixedRates ? 'GST (mixed rates)' : `GST (${lineRates[0] ?? effectiveTaxRate}%)`}</span><span className="font-bold">{formatCurrency(taxAmount, form.currency)}</span></div>
                <div className="flex justify-between text-[15px] pt-1 border-t border-[#D8DDE6]"><span className="font-bold">Total</span><span className="font-bold text-[#0176D3]">{formatCurrency(total, form.currency)}</span></div>
              </div>

              <div className="pt-2 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saving ? 'Saving…' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {receiptPreset && (
        <ReceiptModal
          accounts={accounts}
          contacts={contacts}
          preset={receiptPreset}
          onClose={() => setReceiptPreset(null)}
          onSaved={() => {
            setReceiptPreset(null);
            setReceiptsKey((k) => k + 1);
            alertService.success('Receipt recorded');
            dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 }));
          }}
        />
      )}
      {creditInvoice && (
        <CreditNoteModal
          invoice={creditInvoice}
          onClose={() => setCreditInvoice(null)}
          onSaved={() => { setCreditInvoice(null); setReceiptsKey((k) => k + 1); dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 })); }}
        />
      )}
      {installmentInvoice && (
        <InstallmentModal
          invoice={installmentInvoice}
          onClose={() => setInstallmentInvoice(null)}
          onSaved={() => { setInstallmentInvoice(null); dispatch(fetchCrmInvoices({ status: statusFilter || undefined, limit: 100 })); }}
        />
      )}
      {paymentModalInvoice && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPaymentModalInvoice(null)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-sm z-10 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Record Payment</h3>
              <button onClick={() => setPaymentModalInvoice(null)} className="text-[#706E6B] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>
            <form onSubmit={handleRecordPayment} className="p-4 sm:p-6 space-y-4">
              <p className="text-[12px] text-[#706E6B]">
                {paymentModalInvoice.invoiceNumber} — balance due {formatCurrency((paymentModalInvoice.totalAmount || 0) - (paymentModalInvoice.amountPaid || 0) - (paymentModalInvoice.amountCredited || 0), paymentModalInvoice.currency)}
              </p>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Amount</label>
                <input
                  type="number" min="0" step="0.01" required
                  value={paymentForm.amount}
                  onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Paid Date (optional)</label>
                <input
                  type="date"
                  value={paymentForm.paidDate}
                  onChange={e => setPaymentForm(f => ({ ...f, paidDate: e.target.value }))}
                  className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                />
              </div>
              <div className="pt-2 flex gap-3">
                <button type="button" onClick={() => setPaymentModalInvoice(null)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={recordingPayment} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {recordingPayment ? 'Saving…' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {paymentLinkModalInvoice && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPaymentLinkModalInvoice(null)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-sm z-10 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Online Payment Link</h3>
              <button onClick={() => setPaymentLinkModalInvoice(null)} className="text-[#706E6B] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-[12px] text-[#706E6B]">
                {paymentLinkModalInvoice.invoiceNumber} — {formatCurrency(paymentLinkModalInvoice.totalAmount, paymentLinkModalInvoice.currency)}
              </p>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Gateway</label>
                <select
                  value={paymentLinkGateway}
                  onChange={e => setPaymentLinkGateway(e.target.value)}
                  className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                >
                  <option value="razorpay">Razorpay</option>
                  <option value="stripe">Stripe (International)</option>
                  <option value="cashfree">Cashfree</option>
                  <option value="payu">PayU</option>
                  <option value="ccavenue">CCAvenue</option>
                </select>
                <p className="text-[11px] text-[#706E6B]">Uses the gateway credentials configured under Settings → Integrations.</p>
              </div>

              {paymentLinkModalInvoice.paymentLink?.url && paymentLinkModalInvoice.paymentLink.gateway === paymentLinkGateway && (
                <div className="p-3 bg-[#F4F6F9] rounded border border-[#D8DDE6] space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className={cn('sf-badge', paymentLinkModalInvoice.paymentLink.status === 'paid' ? 'sf-badge-success' : 'sf-badge-warning')}>
                      {paymentLinkModalInvoice.paymentLink.status.toUpperCase()}
                    </span>
                    <button onClick={() => handleCopyPaymentLink(paymentLinkModalInvoice.paymentLink.url)} className="text-[11px] font-bold text-[#0176D3] flex items-center gap-1 hover:underline">
                      <Copy size={12} /> Copy Link
                    </button>
                  </div>
                  <a href={paymentLinkModalInvoice.paymentLink.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#0176D3] break-all hover:underline">
                    {paymentLinkModalInvoice.paymentLink.url}
                  </a>
                </div>
              )}

              <div className="pt-2 flex gap-3">
                <button type="button" onClick={() => setPaymentLinkModalInvoice(null)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Close</button>
                <button type="button" onClick={handleGeneratePaymentLink} disabled={generatingLink} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {generatingLink ? 'Generating…' : 'Generate Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UnpaidReport({ report, loading, onRefresh }) {
  if (loading && !report) {
    return (
      <div className="sf-card p-16 text-center">
        <Loader className="mx-auto animate-spin text-[#0176D3]" size={28} />
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button onClick={onRefresh} disabled={loading} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[12px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2] transition-all flex items-center gap-2 disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sf-card p-4">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Total Unpaid</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(report.totalUnpaid, report.invoices[0]?.currency || 'INR')}</p>
          <p className="text-[11px] text-[#706E6B] mt-1">{report.count} invoice{report.count === 1 ? '' : 's'}</p>
        </div>
        <div className="sf-card p-4">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider flex items-center gap-1">
            <AlertTriangle size={11} className="text-[#BA0517]" /> Overdue Amount
          </p>
          <p className="text-2xl font-bold text-[#BA0517]">{formatCurrency(report.totalOverdue, report.invoices[0]?.currency || 'INR')}</p>
          <p className="text-[11px] text-[#706E6B] mt-1">{report.overdueCount} overdue</p>
        </div>
        <div className="sf-card p-4">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Top Product</p>
          <p className="text-lg font-bold text-[#2E2E2E]">{report.byProduct[0]?.product || '—'}</p>
          <p className="text-[11px] text-[#706E6B] mt-1">{report.byProduct[0] ? formatCurrency(report.byProduct[0].amount, report.invoices[0]?.currency || 'INR') : ''}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="sf-card">
          <div className="sf-card-header"><h2 className="sf-card-title">Unpaid by Product</h2></div>
          <div className="sf-card-body space-y-3">
            {report.byProduct.length > 0 ? report.byProduct.map(p => (
              <div key={p.product} className="flex items-center justify-between text-[13px]">
                <span className="font-medium text-[#2E2E2E]">{p.product}</span>
                <span className="font-bold tabular-nums">{formatCurrency(p.amount, report.invoices[0]?.currency || 'INR')}</span>
              </div>
            )) : <p className="text-[12px] text-[#706E6B]">No unpaid invoices.</p>}
          </div>
        </div>
        <div className="sf-card">
          <div className="sf-card-header"><h2 className="sf-card-title">Unpaid by Client</h2></div>
          <div className="sf-card-body space-y-3">
            {report.byClient.length > 0 ? report.byClient.map(c => (
              <div key={c.client} className="flex items-center justify-between text-[13px]">
                <span className="font-medium text-[#2E2E2E]">{c.client}</span>
                <span className="font-bold tabular-nums">{formatCurrency(c.amount, report.invoices[0]?.currency || 'INR')}</span>
              </div>
            )) : <p className="text-[12px] text-[#706E6B]">No unpaid invoices.</p>}
          </div>
        </div>
      </div>

      <div className="sf-table-container">
        <table className="sf-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Client</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Days Overdue</th>
            </tr>
          </thead>
          <tbody>
            {report.invoices.length > 0 ? report.invoices.map(inv => (
              <tr key={inv._id}>
                <td className="font-bold text-[#2E2E2E]">{inv.invoiceNumber}</td>
                <td>{inv.client}</td>
                <td className="text-[#706E6B]">{inv.product}</td>
                <td className="font-bold tabular-nums">{formatCurrency(inv.totalAmount, inv.currency)}</td>
                <td><span className={cn('sf-badge', inv.isOverdue ? 'sf-badge-error' : 'sf-badge-warning')}>{inv.isOverdue ? 'OVERDUE' : 'SENT'}</span></td>
                <td>{new Date(inv.dueDate).toLocaleDateString()}</td>
                <td className={inv.daysOverdue > 0 ? 'font-bold text-[#BA0517]' : 'text-[#706E6B]'}>{inv.daysOverdue > 0 ? `${inv.daysOverdue}d` : '—'}</td>
              </tr>
            )) : (
              <tr><td colSpan={7} className="py-16 text-center text-[#706E6B] text-sm">No unpaid invoices — all caught up.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const GATEWAY_LABELS = { razorpay: 'Razorpay', stripe: 'Stripe', cashfree: 'Cashfree', payu: 'PayU', ccavenue: 'CCAvenue' };

function PaymentLogsView({ logs, loading, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[#706E6B]">Every payment-gateway webhook event received for your invoices — successes and rejected/unverified attempts alike.</p>
        <button onClick={onRefresh} disabled={loading} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[12px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2] transition-all flex items-center gap-2 disabled:opacity-50 shrink-0">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="sf-card overflow-x-auto">
        {loading && logs.length === 0 ? (
          <div className="py-16 text-center"><Loader className="mx-auto animate-spin text-[#0176D3]" size={28} /></div>
        ) : logs.length === 0 ? (
          <div className="py-24 text-center">
            <Link2 className="mx-auto mb-4 opacity-10" size={48} />
            <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">No payment events yet</p>
          </div>
        ) : (
          <table className="sf-table w-full">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Gateway</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Reference</th>
                <th>Detail</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log._id}>
                  <td className="font-bold text-[#2E2E2E]">{log.invoiceNumber || '—'}</td>
                  <td className="text-[12px] font-bold text-[#706E6B]">{GATEWAY_LABELS[log.gateway] || log.gateway}</td>
                  <td className="font-bold tabular-nums">{formatCurrency(log.amount, log.currency || 'INR')}</td>
                  <td><span className={cn('sf-badge', log.status === 'success' ? 'sf-badge-success' : 'sf-badge-error')}>{log.status.toUpperCase()}</span></td>
                  <td className="text-[11px] text-[#706E6B] font-mono">{log.reference || '—'}</td>
                  <td className="text-[11px] text-[#BA0517]">{log.failureReason || ''}</td>
                  <td className="text-[12px] text-[#706E6B]">{new Date(log.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
